// PHY-02 (rear port near the wall) — README.md.

import type { BuildContext } from '../context';
import type { Issue, RuleId } from '../types';
import { CHANNEL_NAME, issueId } from '../copy';
import { raycastPolygon } from '../geometry';

const MIN_GAP_M = 0.15;

export function checkPhy02(ctx: BuildContext): { issues: Issue[]; unknown: { rule: RuleId; productIds: string[] }[] } {
  const issues: Issue[] = [];
  const unknownProductIds = new Set<string>();
  const channelled = new Set([...ctx.bedIds, ...ctx.overheadIds]);

  for (const entry of ctx.doc.speakers) {
    if (!channelled.has(entry.id)) continue;
    const product = entry.productId ? ctx.catalog.get(entry.productId) : undefined;
    if (!product || product.specs.kind !== 'speaker') continue; // not bound — not applicable

    const port = product.specs.port;
    if (!port) { unknownProductIds.add(product.id); continue; }
    if (port.v !== 'rear') continue;

    // Local +Z is front (speakerFacing.ts); back is the opposite direction.
    const front = { x: Math.sin(entry.rotationY), z: Math.cos(entry.rotationY) };
    const back = { x: -front.x, z: -front.z };
    const origin = { x: entry.position[0], z: entry.position[2] };
    const hit = raycastPolygon(origin, back, ctx.roomPolygon);
    if (!Number.isFinite(hit)) continue; // degenerate room — not applicable

    const depthM = product.body.d / 1000;
    const gap = hit - depthM / 2;
    if (gap >= MIN_GAP_M) continue;

    const channel = ctx.channels[entry.id];
    issues.push({
      id: issueId('PHY-02', [entry.id]),
      rule: 'PHY-02',
      severity: 'warning',
      text: `${CHANNEL_NAME[channel]} is ${Math.round(gap * 100)} cm from the wall.`,
      fix: 'Move it at least 15 cm from the wall.',
      subjects: [entry.id],
    });
  }

  const unknown = unknownProductIds.size > 0
    ? [{ rule: 'PHY-02' as RuleId, productIds: [...unknownProductIds].sort() }]
    : [];
  return { issues, unknown };
}
